// src/pages/Campaigns/VolunteerManagement/OutreachEmailWizard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper,
  TextField, Checkbox, List, ListItem, ListItemText, ListItemIcon,
  Alert, CircularProgress, Divider, Radio, RadioGroup, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Select, MenuItem, FormControl, InputLabel, Card, CardContent,
  Pagination, InputAdornment, Grid
} from '@mui/material';
import { 
  CloudUpload, 
  Mail, 
  Delete, 
  Add, 
  Image as ImageIcon, 
  Visibility, 
  Search as SearchIcon, 
  ArrowBack,
  Business 
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const steps = ['Select Recipients', 'Connect Gmail', 'Compose', 'Review & Send'];

const OutreachEmailWizard = () => {
  const { chatbotId, eventId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  // Data State
  const [event, setEvent] = useState(null);
  
  // Volunteer State
  const [volunteers, setVolunteers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Upload State
  const [uploadFile, setUploadFile] = useState(null);
  
  // CRM State
  const [crmContacts, setCrmContacts] = useState([]);
  const [selectedCrmIds, setSelectedCrmIds] = useState([]);

  const [selectionMode, setSelectionMode] = useState('existing'); // existing, upload, crm
  const [gmailConnected, setGmailConnected] = useState(false);
  const [checkingGmail, setCheckingGmail] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  
  // Email content state
  const [emailContent, setEmailContent] = useState({
    subject: '',
    body: '',
    logoUrl: '',
    disclaimer: '',
    selectedDisclaimerId: ''
  });
  
  // Disclaimer Bank State
  const [disclaimerBank, setDisclaimerBank] = useState([]);
  const [disclaimerDialog, setDisclaimerDialog] = useState(false);
  const [newDisclaimer, setNewDisclaimer] = useState({ name: '', text: '' });
  const [previewDialog, setPreviewDialog] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadEvent();
    loadVolunteers();
    checkGmail();
    loadDisclaimerBank();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      setGmailConnected(true);
      setActiveStep(2);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Effect to load volunteers when page/search changes
  useEffect(() => {
    if (activeStep === 0 && selectionMode === 'existing') {
      const timeoutId = setTimeout(() => {
        loadVolunteers();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [page, searchTerm, selectionMode]);

  // Effect to load CRM contacts when mode changes
  useEffect(() => {
    if (selectionMode === 'crm' && crmContacts.length === 0) {
        loadCrmContacts();
    }
  }, [selectionMode]);

  const loadEvent = async () => {
    try {
      const res = await axiosInstance.get('/api/events', { params: { chatbot_id: chatbotId } });
      const foundEvent = res.data.data.find(e => e.id === parseInt(eventId));
      setEvent(foundEvent);
      
      if (foundEvent) {
        const eventDateRange = `${new Date(foundEvent.event_start_date).toLocaleDateString()} - ${new Date(foundEvent.event_end_date).toLocaleDateString()}`;
        setEmailContent(prev => ({
          ...prev,
          subject: `Volunteer Opportunity: ${foundEvent.name}`,
          body: `Hello,\n\nWe need your help for our ${foundEvent.name} event on ${eventDateRange} at ${foundEvent.location}.\n\nPlease confirm your availability using the link below.\n\nThank you!`
        }));
      }
    } catch (e) { console.error(e); }
  };

  const loadVolunteers = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/volunteers', { 
        params: { 
          chatbot_id: chatbotId,
          page: page,
          per_page: 10,
          search: searchTerm,
          status: 'ACTIVE'
        } 
      });
      setVolunteers(res.data.data || []);
      setTotalPages(res.data.pagination?.pages || 1);
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoading(false);
    }
  };

  const loadCrmContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/crm/contacts', { params: { chatbot_id: chatbotId, per_page: 1000 } });
      // Filter for contacts with emails
      const valid = (res.data.data || []).filter(c => c.email);
      setCrmContacts(valid);
    } catch (e) { 
      console.error(e); 
    } finally { 
      setLoading(false); 
    }
  };

  const checkGmail = async () => {
    setCheckingGmail(true);
    try {
      const res = await axiosInstance.get('/api/event-gmail/status', { params: { chatbot_id: chatbotId } });
      setGmailConnected(res.data.connected);
    } catch (e) { 
      console.error(e); 
      setGmailConnected(false);
    } finally {
      setCheckingGmail(false);
    }
  };

  const loadDisclaimerBank = async () => {
    try {
      const res = await axiosInstance.get('/api/surveys/disclaimers', { 
        params: { chatbot_id: chatbotId } 
      });
      setDisclaimerBank(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const saveDisclaimer = async () => {
    if (!newDisclaimer.name || !newDisclaimer.text) {
      alert('Please fill in both name and text');
      return;
    }
    
    try {
      setLoading(true);
      await axiosInstance.post('/api/surveys/disclaimers', newDisclaimer, {
        params: { chatbot_id: chatbotId }
      });
      await loadDisclaimerBank();
      setDisclaimerDialog(false);
      setNewDisclaimer({ name: '', text: '' });
      alert('Disclaimer saved successfully!');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save disclaimer');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingLogo(true);
      const res = await axiosInstance.post('/api/surveys/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { chatbot_id: chatbotId }
      });
      
      setEmailContent(prev => ({
        ...prev,
        logoUrl: res.data.logo_url
      }));
      
      alert('Logo uploaded successfully!');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setEmailContent(prev => ({ ...prev, logoUrl: '' }));
  };

  const handleConnect = async () => {
    try {
      const response = await axiosInstance.get('/api/event-gmail/auth/start', {
        params: { 
          chatbot_id: chatbotId,
          event_id: eventId 
        }
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else if (response.data.already_connected) {
        setGmailConnected(true);
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Error initiating Gmail connection:', error);
      alert('Failed to connect to Gmail');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      setLoading(true);
      await axiosInstance.post('/api/volunteers/upload-csv', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { chatbot_id: chatbotId } 
      });
      
      await loadVolunteers();
      setUploadFile(null);
      setSelectionMode('existing');
      alert('Upload successful!');
    } catch (e) { 
      alert(e.response?.data?.message || 'Upload failed'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/outreach/campaign', {
        event_id: parseInt(eventId),
        volunteer_ids: selectionMode === 'existing' ? selectedIds : [],
        crm_contact_ids: selectionMode === 'crm' ? selectedCrmIds : [],
        send_email: true,
        send_sms: false,
        campaign_name: `Email Campaign for ${event?.name}`,
        email_subject: emailContent.subject,
        email_body: emailContent.body + (emailContent.disclaimer ? `\n\n${emailContent.disclaimer}` : '')
      }, { params: { chatbot_id: chatbotId } });
      
      const data = response.data;
      
      // Check for Gmail errors
      if (data.gmail_errors && data.gmail_errors.length > 0) {
        const errorMessage = `Email Campaign completed with errors:\n\n` +
          `✓ Sent: ${data.stats?.success_count || 0}\n` +
          `✗ Failed: ${data.stats?.failed_count || 0}\n\n` +
          `Errors:\n${data.gmail_errors.join('\n')}`;
        
        alert(errorMessage);
      } else if (data.stats?.success_count > 0) {
        alert(`Email Campaign Sent Successfully! ${data.stats.success_count} email(s) sent.`);
      } else {
        alert('Email Campaign completed but no emails were sent.');
      }
      
      navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`);
    } catch (e) { 
      const errorData = e.response?.data;
      
      // Check if we need to reconnect Gmail
      if (errorData?.needs_reconnect || e.response?.status === 401) {
        const confirmReconnect = window.confirm(
          'Gmail authorization has expired. Would you like to reconnect your Gmail account now?'
        );
        
        if (confirmReconnect) {
          setGmailConnected(false);
          setActiveStep(1);
          setTimeout(() => {
            handleConnect();
          }, 500);
        }
      } else if (errorData?.gmail_errors && errorData.gmail_errors.length > 0) {
        const errorMessage = `Failed to send emails:\n\n${errorData.gmail_errors.join('\n')}`;
        alert(errorMessage);
      } else {
        alert(errorData?.message || 'Failed to send emails');
      }
    } finally { 
      setLoading(false); 
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
        if (selectionMode === 'existing' && selectedIds.length === 0) return alert('Select at least one volunteer');
        if (selectionMode === 'crm' && selectedCrmIds.length === 0) return alert('Select at least one CRM contact');
    }
    if (activeStep === 1 && !gmailConnected) return alert('Please connect Gmail first');
    if (activeStep === 2 && (!emailContent.subject || !emailContent.body)) return alert('Please fill in email subject and body');
    setActiveStep((prev) => prev + 1);
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      const currentPageIds = volunteers.map(v => v.id);
      setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
      setSelectAll(false);
    } else {
      const currentPageIds = volunteers.map(v => v.id);
      setSelectedIds(prev => {
        const newSelected = [...prev];
        currentPageIds.forEach(id => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });
        return newSelected;
      });
      setSelectAll(true);
    }
  };

  const generateEmailPreview = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; background: #ffffff; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 30px; text-align: center; }
          .logo { max-width: 150px; margin-bottom: 20px; }
          .content { padding: 40px 30px; }
          .event-card { background: #f8f9fa; border-left: 4px solid #667eea; padding: 20px; margin: 25px 0; border-radius: 4px; }
          .cta-button { display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white !important; padding: 16px 40px; text-decoration: none; border-radius: 50px; font-weight: 600; margin: 20px 0; }
          .disclaimer { font-size: 12px; color: #666; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
          .footer { background: #f8f9fa; padding: 30px; text-align: center; color: #666; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            ${emailContent.logoUrl ? `<img src="${emailContent.logoUrl}" alt="Logo" class="logo" />` : ''}
            <h1>Volunteer Opportunity</h1>
          </div>
          <div class="content">
            <p>Hello <strong>{{name}}</strong>,</p>
            <div class="event-card">
              <h2>${event?.name || 'Event'}</h2>
              <p><strong>📅 Date:</strong> ${event ? `${new Date(event.event_start_date).toLocaleDateString()} - ${new Date(event.event_end_date).toLocaleDateString()}` : ''}</p>
              <p><strong>📍 Location:</strong> ${event?.location || ''}</p>
            </div>
            <div>${emailContent.body.replace(/\n/g, '<br>')}</div>
            <div style="text-align: center;">
              <a href="{{availability_link}}" class="cta-button">Confirm Availability</a>
            </div>
            ${emailContent.disclaimer ? `<div class="disclaimer">${emailContent.disclaimer.replace(/\n/g, '<br>')}</div>` : ''}
          </div>
          <div class="footer">
            <p>Thank you for your dedication to volunteering!</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // --- Step Content Renderers ---

  const renderSelectStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Select Recipients</Typography>
      
      <Box sx={{ mb: 2 }}>
        <RadioGroup row value={selectionMode} onChange={(e) => setSelectionMode(e.target.value)}>
          <FormControlLabel value="existing" control={<Radio />} label="Existing Volunteers" />
          <FormControlLabel value="upload" control={<Radio />} label="Upload CSV/Excel" />
          <FormControlLabel value="crm" control={<Radio />} label="CRM Contacts" />
        </RadioGroup>
      </Box>

      {selectionMode === 'upload' ? (
        <Box sx={{ border: '1px dashed grey', p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
            Choose File
            <input type="file" hidden accept=".csv,.xlsx" onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
          {uploadFile && <Typography sx={{ mt: 1 }}>{uploadFile.name}</Typography>}
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || loading} sx={{ mt: 2, display: 'block', mx: 'auto' }}>
            {loading ? 'Uploading...' : 'Upload & Process'}
          </Button>
        </Box>
      ) : selectionMode === 'crm' ? (
         // CRM UI
         <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
            {loading ? <Box p={3} textAlign="center"><CircularProgress /></Box> :
             crmContacts.length === 0 ? (
                <Box p={3} textAlign="center"><Typography color="textSecondary">No CRM contacts with emails found.</Typography></Box>
             ) : (
                <List>
                  {crmContacts.map((c) => (
                    <ListItem key={c.id} button onClick={() => {
                        setSelectedCrmIds(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                    }}>
                      <ListItemIcon><Checkbox checked={selectedCrmIds.includes(c.id)} /></ListItemIcon>
                      <ListItemText 
                        primary={`${c.first_name} ${c.last_name || ''}`} 
                        secondary={c.email + (c.company_name ? ` • ${c.company_name}` : '')} 
                      />
                    </ListItem>
                  ))}
                </List>
             )}
         </Paper>
      ) : (
        <Box>
          {/* Existing Volunteers: Search and Select All */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or phone"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAllToggle}
                    disabled={volunteers.length === 0}
                  />
                }
                label="Select All on This Page"
              />
            </Grid>
          </Grid>

          <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
            {loading ? (
              <Box p={3} textAlign="center"><CircularProgress /></Box>
            ) : volunteers.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">No volunteers found. Try uploading a CSV or adjusting your search.</Typography>
              </Box>
            ) : (
              <List>
                {volunteers.map((v) => (
                  <ListItem key={v.id} button onClick={() => {
                    setSelectedIds(prev => prev.includes(v.id) ? prev.filter(id => id !== v.id) : [...prev, v.id]);
                  }}>
                    <ListItemIcon>
                      <Checkbox checked={selectedIds.includes(v.id)} />
                    </ListItemIcon>
                    <ListItemText primary={v.full_name} secondary={`${v.email} | ${v.phone_number}`} />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}
      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
        Selected: {selectionMode === 'crm' ? selectedCrmIds.length : selectedIds.length} recipient(s)
      </Typography>
    </Box>
  );

  const renderConnectStep = () => (
    <Box textAlign="center" py={4}>
      <Mail sx={{ fontSize: 60, color: gmailConnected ? 'success.main' : 'grey.400', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {gmailConnected ? 'Gmail Connected' : 'Connect your Gmail Account'}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        We use your Gmail account to send personalized volunteer invitations.
      </Typography>
      {checkingGmail ? (
        <CircularProgress />
      ) : !gmailConnected ? (
        <Button variant="contained" onClick={handleConnect}>
          Connect Gmail
        </Button>
      ) : (
        <Alert severity="success" sx={{ display: 'inline-flex' }}>Account Ready</Alert>
      )}
    </Box>
  );

  const renderComposeStep = () => (
    <Box>
      {/* Logo Upload Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ImageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Email Logo (Optional)
          </Typography>
          
          {emailContent.logoUrl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <img 
                src={emailContent.logoUrl} 
                alt="Logo" 
                style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
              />
              <IconButton onClick={removeLogo} color="error">
                <Delete />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={uploadingLogo ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploadingLogo}
              sx={{ mt: 2 }}
            >
              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
            </Button>
          )}
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            Recommended: PNG or JPG, max 2MB
          </Typography>
        </CardContent>
      </Card>

      {/* Email Subject */}
      <TextField 
        fullWidth 
        label="Email Subject" 
        sx={{ mb: 3 }}
        value={emailContent.subject}
        onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
      />

      {/* Email Body */}
      <TextField 
        fullWidth 
        multiline 
        rows={6} 
        label="Email Body"
        value={emailContent.body}
        onChange={(e) => setEmailContent({ ...emailContent, body: e.target.value })}
        helperText="A personalized availability link will be automatically added"
        sx={{ mb: 3 }}
      />

      {/* Disclaimer Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Disclaimer (Optional)</Typography>
            <Button startIcon={<Add />} onClick={() => setDisclaimerDialog(true)} size="small">
              Save to Bank
            </Button>
          </Box>

          {disclaimerBank.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Choose from Bank</InputLabel>
              <Select
                value={emailContent.selectedDisclaimerId}
                label="Choose from Bank"
                onChange={(e) => {
                  const selected = disclaimerBank.find(d => d.id === e.target.value);
                  setEmailContent({
                    ...emailContent,
                    selectedDisclaimerId: e.target.value,
                    disclaimer: selected ? selected.text : ''
                  });
                }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {disclaimerBank.map((disc) => (
                  <MenuItem key={disc.id} value={disc.id}>{disc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Disclaimer Text"
            value={emailContent.disclaimer}
            onChange={(e) => setEmailContent({ ...emailContent, disclaimer: e.target.value })}
            placeholder="This message is confidential..."
          />
        </CardContent>
      </Card>

      {/* Preview Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button variant="outlined" startIcon={<Visibility />} onClick={() => setPreviewDialog(true)}>
          Preview Email
        </Button>
      </Box>

      {/* Disclaimer Bank Dialog */}
      <Dialog open={disclaimerDialog} onClose={() => setDisclaimerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Disclaimer to Bank</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Disclaimer Name"
            value={newDisclaimer.name}
            onChange={(e) => setNewDisclaimer({ ...newDisclaimer, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Disclaimer Text"
            value={newDisclaimer.text}
            onChange={(e) => setNewDisclaimer({ ...newDisclaimer, text: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisclaimerDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDisclaimer} disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Box dangerouslySetInnerHTML={{ __html: generateEmailPreview() }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You are about to send emails to <strong>{selectionMode === 'crm' ? selectedCrmIds.length : selectedIds.length}</strong> recipients for <strong>{event?.name}</strong>.
      </Alert>
      
      {emailContent.logoUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Logo:</Typography>
          <img src={emailContent.logoUrl} alt="Logo" style={{ maxHeight: 60, marginTop: 8 }} />
        </Box>
      )}
      
      <Typography variant="subtitle2">Subject:</Typography>
      <Typography gutterBottom>{emailContent.subject}</Typography>
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2">Body Preview:</Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', mb: 2 }}>
        {emailContent.body}
      </Paper>
      
      {emailContent.disclaimer && (
        <>
          <Typography variant="subtitle2">Disclaimer:</Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', fontSize: '0.875rem' }}>
            {emailContent.disclaimer}
          </Paper>
        </>
      )}
      
      <Alert severity="success" sx={{ mt: 2 }}>
        A unique availability confirmation link will be added to each email automatically.
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">Send Volunteer Email Invitations</Typography>
      </Box>
      
      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderConnectStep()}
        {activeStep === 2 && renderComposeStep()}
        {activeStep === 3 && renderReviewStep()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => activeStep === 0 ? navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`) : setActiveStep(prev => prev - 1)}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSend} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Emails'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>Next</Button>
        )}
      </Box>
    </Box>
  );
};

export default OutreachEmailWizard;